import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  Check,
  GraduationCap,
  Laptop,
  Network,
  Sparkles,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';

interface HeroSectionProps {
  content?: {
    heroTitle?: string;
    heroSubtitle?: string;
    heroImage?: string;
  };
}

const benefits = [
  'Real-time SAP system access',
  'Industry expert mentors',
  'Practical hands-on learning',
  'Career guidance',
];

const learningSteps = [
  { icon: GraduationCap, label: 'Mentor-led SAP concepts' },
  { icon: Laptop, label: 'Real-time system practice' },
  { icon: Network, label: 'Career-focused guidance' },
];

const HeroSection: React.FC<HeroSectionProps> = ({ content }) => {
  const prefersReducedMotion = useReducedMotion();
  const [canShowHeroImage, setCanShowHeroImage] = useState(Boolean(content?.heroImage));
  const title = content?.heroTitle;
  const subtitle =
    content?.heroSubtitle ||
    'Personalized SAP learning with online and offline classes, real-time system access, and mentor support for freshers and professionals.';

  const entrance = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5, ease: 'easeOut' as const },
      };

  return (
    <section className="relative isolate overflow-hidden bg-bg-secondary" aria-labelledby="hero-heading">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-32 top-12 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-primary-200/45 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.035] [background-image:linear-gradient(var(--color-primary-700)_1px,transparent_1px),linear-gradient(90deg,var(--color-primary-700)_1px,transparent_1px)] [background-size:2.5rem_2.5rem]" />
      </div>

      <div className="container-main relative py-14 sm:py-20 lg:py-24 xl:py-28">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:items-center md:gap-10 lg:gap-16 xl:gap-20">
          <motion.div {...entrance} className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary-700">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              SAP career training
            </div>

            {title ? (
              <h1 id="hero-heading" className="mt-6 max-w-3xl text-4xl font-bold tracking-[-0.035em] text-text-primary sm:text-5xl lg:text-[3.35rem] lg:leading-[1.08]">
                {title}
              </h1>
            ) : (
              <h1 id="hero-heading" className="mt-6 max-w-3xl text-4xl font-bold tracking-[-0.035em] text-text-primary sm:text-5xl lg:text-[3.35rem] lg:leading-[1.08]">
                Build your career with <span className="text-primary-700">SAP S/4HANA</span>
                <span className="mt-2 block text-text-secondary">SD, MM &amp; mentorship.</span>
              </h1>
            )}

            <p className="mt-6 max-w-2xl text-base leading-7 text-text-secondary sm:text-lg sm:leading-8">
              {subtitle}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/courses"
                className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 active:bg-primary-800"
              >
                Explore Courses
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
              <Link
                to="/book-demo"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-border-default bg-bg-primary px-5 py-3 text-sm font-semibold text-text-primary transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 active:bg-bg-tertiary"
              >
                <CalendarDays className="h-4 w-4" aria-hidden="true" />
                Book a Demo
              </Link>
            </div>

            <ul className="mt-8 grid max-w-2xl gap-x-6 gap-y-3 text-sm text-text-secondary sm:grid-cols-2" aria-label="Training benefits">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {benefit}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            {...entrance}
            transition={prefersReducedMotion ? undefined : { duration: 0.6, delay: 0.12, ease: 'easeOut' }}
            className="relative mx-auto w-full max-w-xl md:mx-0 md:max-w-none"
          >
            <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-primary-100/70 blur-2xl" aria-hidden="true" />
            <div className="absolute -right-3 -top-3 h-20 w-20 rounded-full border border-primary-200 bg-primary-50" aria-hidden="true" />
            {canShowHeroImage && content?.heroImage ? (
              <div className="relative overflow-hidden rounded-[1.5rem] border border-border-default bg-bg-primary p-2 shadow-xl shadow-primary-950/10">
                <img
                  src={content.heroImage}
                  alt="SAP learning at ShreeGuruCool Technologies"
                  className="aspect-[5/4] w-full rounded-[1.1rem] object-cover"
                  onError={() => setCanShowHeroImage(false)}
                />
                <div className="pointer-events-none absolute inset-x-2 bottom-2 rounded-b-[1.1rem] bg-gradient-to-t from-primary-950/75 via-primary-950/10 to-transparent px-5 pb-5 pt-16 text-white" aria-hidden="true">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-100">ShreeGuruCool learning</p>
                  <p className="mt-1 text-lg font-semibold">SAP training built around practical learning</p>
                </div>
              </div>
            ) : (
              <div className="relative overflow-hidden rounded-[1.5rem] border border-border-default bg-bg-primary p-5 shadow-xl shadow-primary-950/10 sm:p-7">
                <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600" aria-hidden="true" />
                <div className="relative flex items-start justify-between gap-4 border-b border-border-subtle pb-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-sm font-bold text-primary-700 shadow-sm">S/4</div>
                    <div>
                      <p className="text-sm font-semibold text-white">SAP S/4HANA learning path</p>
                      <p className="mt-0.5 text-xs text-primary-100">Guided practice with mentor support</p>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold text-white ring-1 ring-white/20">Practical learning</span>
                </div>

                <div className="relative mt-8 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-primary-100 bg-primary-50 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary-700">SAP SD</p>
                    <p className="mt-2 text-sm font-semibold text-text-primary">Sales &amp; distribution</p>
                    <p className="mt-1 text-xs leading-5 text-text-secondary">Learn processes through structured, practical sessions.</p>
                  </div>
                  <div className="rounded-xl border border-border-subtle bg-bg-secondary p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary">SAP MM</p>
                    <p className="mt-2 text-sm font-semibold text-text-primary">Materials management</p>
                    <p className="mt-1 text-xs leading-5 text-text-secondary">Build confidence with guided hands-on learning.</p>
                  </div>
                </div>

                <div className="relative mt-6 space-y-4 border-t border-border-subtle pt-5">
                  {learningSteps.map(({ icon: Icon, label }, index) => (
                    <div key={label} className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-primary-700">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="text-sm font-medium text-text-secondary">{label}</span>
                      {index < 2 && <span className="ml-auto h-px w-8 bg-primary-100" aria-hidden="true" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <motion.div
              animate={prefersReducedMotion ? undefined : { y: [0, -5, 0] }}
              transition={prefersReducedMotion ? undefined : { duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -bottom-5 -left-3 rounded-xl border border-border-default bg-bg-primary px-4 py-3 shadow-lg shadow-primary-950/10 sm:-left-7"
            >
              <p className="text-xs font-semibold text-text-primary">Online + offline classes</p>
              <p className="mt-0.5 text-xs text-text-muted">Designed for freshers and professionals</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
